/**
 * AdminShell — the Administration area chrome (Story UI-U6 AC1): its OWN settings
 * sidebar REPLACING the app nav (design.md §5), a sticky `Administration / <Seite>`
 * breadcrumb, and a flush-left, full-width content column — same `px-6` page
 * gutter as the app's list pages (Aufgaben/Kunden/Policen/Ablage via ListView),
 * so the admin area lines up with the rest of the app instead of floating in a
 * centered column. Rendered by the admin LAYOUT route (a sibling of the `app`
 * layout — the admin area must NOT show the app nav); the section pages render
 * into `children` (the router Outlet).
 *
 * [ea/layout-alignment] was `mx-auto w-full max-w-7xl px-6 py-8` (centered,
 * width-capped) — dropped `mx-auto`/`max-w-7xl` so every /admin/$section page
 * (roles, members, signature, etc.) starts at the same left position as the
 * rest of the app, matching the reference list-page container.
 *
 * [Source: apps/web/src/app/shell/AppShell.tsx] (layout shell pattern)
 * [Source: apps/web/src/components/data/ListView.tsx] (reference flush-left page container)
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §5]
 */

import type { ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { AdminSidebar } from './AdminSidebar';
import { ADMIN_PAGE_META, isAdminPageKey } from './adminNav';

export function AdminShell({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const section = path.replace(/^\/admin\/?/, '').split('/')[0];
  const title = isAdminPageKey(section) ? ADMIN_PAGE_META[section].title : undefined;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      <AdminSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-6">
          <nav aria-label="Brotkrümel" className="flex items-center gap-1.5 text-sm">
            <span className="text-muted-foreground">Administration</span>
            {title != null && (
              <>
                <span className="text-muted-foreground/50">/</span>
                <span className="font-medium text-foreground">{title}</span>
              </>
            )}
          </nav>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="w-full px-6 py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
